import type { Field } from '@/types/types';
import { validateEmail, validatePhone } from './validation';

export type ValidationState = 'valid' | 'invalid' | 'idle';

export interface FieldValidationOptions {
    field?: Field;
    ui_type?: string;
    input_type?: string;
    required?: boolean;
    value?: any;
    isDirty?: boolean;
    min?: number;
    max?: number;
}

/**
 * Determine if a field is required
 */
export function isFieldRequired(field?: Field, explicitRequired?: boolean): boolean {
    return field?.required === true || explicitRequired === true;
}

/**
 * Validate number is within min/max range
 */
export function validateNumberRange(value: any, min?: number, max?: number): { isValid: boolean; error: 'min' | 'max' | null } {
    if (value === null || value === undefined || value === '') {
        return { isValid: true, error: null };
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
        return { isValid: true, error: null };
    }

    if (min !== undefined && numValue < min) {
        return { isValid: false, error: 'min' };
    }

    if (max !== undefined && numValue > max) {
        return { isValid: false, error: 'max' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate field value based on input_type
 */
export function validateFieldValue(value: any, input_type?: string, required?: boolean, min?: number, max?: number): boolean {
    const stringValue = value?.toString().trim() ?? '';

    // If empty and not required, it's valid
    if (!stringValue && !required) {
        return true;
    }

    // If empty and required, it's invalid
    if (!stringValue && required) {
        return false;
    }

    // Type-specific validation based on input_type
    switch (input_type) {
        case 'email':
            return validateEmail(value);
        case 'tel':
        case 'phone':
            return validatePhone(value);
        case 'number':
            // Validate number range
            return validateNumberRange(value, min, max).isValid;
        default:
            // For other types, just check if not empty when required
            return required ? stringValue.length > 0 : true;
    }
}

/**
 * Get validation state for a field
 */
export function getFieldValidationState(options: FieldValidationOptions): ValidationState {
    const {
        field,
        ui_type = field?.ui_type,
        input_type = field?.input_type,
        required = isFieldRequired(field),
        value,
        isDirty = false,
        min,
        max,
    } = options;

    const stringValue = value?.toString().trim() ?? '';
    const isEmpty = !stringValue;

    // If field is empty
    if (isEmpty) {
        if (required && isDirty) {
            return 'invalid';
        }
        return 'idle';
    }

    // Validate the value based on input_type (including min/max for numbers)
    const isValid = validateFieldValue(value, input_type ?? undefined, required, min, max);

    // If valid, always show success (even if not dirty) - for autofill support
    if (isValid) {
        return 'valid';
    }

    // For number range errors, show invalid immediately (user has entered a value)
    if (input_type === 'number' && (min !== undefined || max !== undefined)) {
        return 'invalid';
    }

    // Only show invalid state after field is touched (prevents premature errors while typing)
    if (!isDirty) {
        return 'idle';
    }

    return 'invalid';
}

/**
 * Get validation CSS classes
 */
export function getValidationClasses(state: ValidationState): string {
    switch (state) {
        case 'valid':
            return 'input-success';
        case 'invalid':
            return 'input-error';
        default:
            return '';
    }
}

/**
 * Get validation error message based on input_type
 */
export function getValidationErrorMessage(
    state: ValidationState,
    input_type?: string,
    customMessage?: string | null,
    rangeError?: 'min' | 'max' | null,
    min?: number,
    max?: number,
): string | null {
    if (state !== 'invalid') {
        return null;
    }

    // Return custom message if provided
    if (customMessage) {
        return customMessage;
    }

    // Handle number range errors
    if (input_type === 'number' && rangeError) {
        if (rangeError === 'min' && min !== undefined) {
            return `Value must be at least ${min}`;
        }
        if (rangeError === 'max' && max !== undefined) {
            return `Value must be at most ${max}`;
        }
    }

    // Type-specific error messages based on input_type
    switch (input_type) {
        case 'email':
            return 'Please enter a valid email address';
        case 'tel':
        case 'phone':
            return 'Please enter a valid phone number (10-15 digits)';
        default:
            return 'This field is required';
    }
}

/**
 * Create a complete field validation helper
 * Returns reactive validation state and helpers
 */
export function createFieldValidation(options: FieldValidationOptions) {
    const state = getFieldValidationState(options);
    const classes = getValidationClasses(state);
    const isRequired = isFieldRequired(options.field);
    // Convert null to undefined for input_type
    const input_type = (options.input_type ?? options.field?.input_type) || undefined;

    // Get range error for number fields
    const rangeValidation = input_type === 'number' ? validateNumberRange(options.value, options.min, options.max) : { error: null };

    const errorMessage = getValidationErrorMessage(
        state,
        input_type,
        options.field ? null : undefined,
        rangeValidation.error,
        options.min,
        options.max,
    );

    return {
        state,
        classes,
        errorMessage,
        isRequired,
        isValid: state === 'valid',
        isInvalid: state === 'invalid',
        isIdle: state === 'idle',
    };
}
